import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Product } from '../components/ProductCard';
import { useUser } from './UserContext';

export interface CartItem {
  product: Product;
  quantity: number;
}

export type OrderStatus = 'processing' | 'shipped' | 'delivered' | 'returned' | 'return_requested';

export interface Order {
  id: string;
  items: CartItem[];
  total: number;
  status: OrderStatus;
  orderDate: string;
  deliveryDate: string;
}

interface CartContextType {
  items: CartItem[];
  orders: Order[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;
  placeOrder: () => Promise<Order>;
  loadingOrders: boolean;
  refreshOrders: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'wehkamp_cart';
const ORDERS_STORAGE_KEY = 'wehkamp_orders';
const MIGRATED_KEY = 'wehkamp_orders_migrated';

function calculateDeliveryDate(orderDate: Date): Date {
  const delivery = new Date(orderDate);
  const dayOfWeek = orderDate.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday

  if (dayOfWeek === 5) {
    // Friday -> Tuesday (4 days)
    delivery.setDate(delivery.getDate() + 4);
  } else if (dayOfWeek === 6) {
    // Saturday -> Tuesday (3 days)
    delivery.setDate(delivery.getDate() + 3);
  } else if (dayOfWeek === 0) {
    // Sunday -> Tuesday (2 days)
    delivery.setDate(delivery.getDate() + 2);
  } else {
    // Monday-Thursday -> next day
    delivery.setDate(delivery.getDate() + 1);
  }

  return delivery;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

function generateOrderId(): string {
  return 'WK' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).substring(2, 6).toUpperCase();
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { user, getAuthHeaders } = useUser();
  const [items, setItems] = useState<CartItem[]>(() => {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Fetch orders from API when user logs in
  const fetchOrders = useCallback(async () => {
    if (!user?.email) {
      // If not logged in, show localStorage orders
      const stored = localStorage.getItem(ORDERS_STORAGE_KEY);
      setOrders(stored ? JSON.parse(stored) : []);
      return;
    }

    setLoadingOrders(true);
    try {
      // Get Firebase auth token for secure API access
      const authHeaders = await getAuthHeaders();
      const response = await fetch('/api/orders', {
        headers: {
          ...authHeaders,
        },
      });
      const data = await response.json();

      if (data.orders) {
        // Convert API order format to our Order format
        const apiOrders: Order[] = data.orders.map((o: Record<string, unknown>) => ({
          id: o.id as string,
          items: ((o.items as Array<Record<string, unknown>>) || []).map((item) => ({
            product: {
              id: item.product_id as string,
              name: item.name as string,
              price: item.price as number,
              image_url: item.image_url as string || '',
              brand: '',
              category: '',
            },
            quantity: item.quantity as number,
          })),
          total: o.total as number,
          status: o.status as OrderStatus,
          orderDate: (o.order_date_formatted as string) || '',
          deliveryDate: (o.delivery_date_formatted as string) || '',
        }));
        setOrders(apiOrders);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      // Fallback to localStorage
      const stored = localStorage.getItem(ORDERS_STORAGE_KEY);
      setOrders(stored ? JSON.parse(stored) : []);
    } finally {
      setLoadingOrders(false);
    }
  }, [user?.email, getAuthHeaders]);

  // Migrate localStorage orders when user logs in
  const migrateLocalOrders = useCallback(async () => {
    if (!user?.email) return;

    // Check if already migrated
    const migrated = localStorage.getItem(MIGRATED_KEY);
    if (migrated === user.email) return;

    const localOrders = localStorage.getItem(ORDERS_STORAGE_KEY);
    if (!localOrders) {
      localStorage.setItem(MIGRATED_KEY, user.email);
      return;
    }

    const ordersToMigrate = JSON.parse(localOrders);
    if (ordersToMigrate.length === 0) {
      localStorage.setItem(MIGRATED_KEY, user.email);
      return;
    }

    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch('/api/orders/migrate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({ orders: ordersToMigrate }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Migrated ${data.migrated} orders to Firestore`);
        localStorage.setItem(MIGRATED_KEY, user.email);
        // Clear localStorage orders after successful migration
        localStorage.removeItem(ORDERS_STORAGE_KEY);
        // Refresh orders from API
        fetchOrders();
      }
    } catch (err) {
      console.error('Failed to migrate orders:', err);
    }
  }, [user?.email, fetchOrders, getAuthHeaders]);

  // When user changes, fetch their orders
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Migrate local orders after fetch
  useEffect(() => {
    if (user?.email) {
      migrateLocalOrders();
    }
  }, [user?.email, migrateLocalOrders]);

  const addToCart = (product: Product, quantity: number = 1) => {
    setItems(current => {
      const existing = current.find(item => item.product.id === product.id);
      if (existing) {
        return current.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...current, { product, quantity }];
    });
  };

  const removeFromCart = (productId: string) => {
    setItems(current => current.filter(item => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setItems(current =>
      current.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getCartTotal = () => {
    return items.reduce((total, item) => total + item.product.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  const placeOrder = async (): Promise<Order> => {
    const orderDate = new Date();
    const deliveryDate = calculateDeliveryDate(orderDate);

    // If user is logged in, save to Firestore via API
    if (user?.email) {
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch('/api/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            items: items.map(item => ({
              product_id: item.product.id,
              name: item.product.name,
              price: item.product.price,
              quantity: item.quantity,
              image_url: item.product.image_url,
            })),
            total: getCartTotal(),
          }),
        });

        const data = await response.json();

        if (data.success && data.order) {
          const order: Order = {
            id: data.order.id,
            items: [...items],
            total: getCartTotal(),
            status: 'processing',
            orderDate: data.order.order_date_formatted || formatDate(orderDate),
            deliveryDate: data.order.delivery_date_formatted || formatDate(deliveryDate),
          };

          setOrders(current => [order, ...current]);
          clearCart();
          return order;
        }
      } catch (err) {
        console.error('Failed to create order via API:', err);
        // Fall through to localStorage
      }
    }

    // Fallback: save to localStorage
    const order: Order = {
      id: generateOrderId(),
      items: [...items],
      total: getCartTotal(),
      status: 'processing',
      orderDate: formatDate(orderDate),
      deliveryDate: formatDate(deliveryDate),
    };

    setOrders(current => {
      const newOrders = [order, ...current];
      localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(newOrders));
      return newOrders;
    });
    clearCart();

    return order;
  };

  return (
    <CartContext.Provider value={{
      items,
      orders,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount,
      placeOrder,
      loadingOrders,
      refreshOrders: fetchOrders,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
